import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { PaymentsAPI, AuthAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  CheckCircle2, Loader2, ArrowRight, Sparkles, Crown, KeyRound,
} from "lucide-react";

const PLAN_LABELS = {
  pro_monthly: { name: "Pro Monthly", price: 20, period: "month" },
  creator_monthly: { name: "Creator Monthly", price: 45, period: "month" },
  halftone_lifetime: { name: "Halftone Lifetime", price: 50, period: "one-time" },
  all_access_lifetime: { name: "All-Access Lifetime", price: 100, period: "one-time" },
};

export default function PaymentSuccessPage() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = React.useState("loading"); // loading | paid | pending | error
  const [data, setData] = React.useState(null);
  const [attempts, setAttempts] = React.useState(0);
  const { refreshUsage, setUser } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let attemptsLocal = 0;
    async function poll() {
      if (cancelled) return;
      try {
        const r = await PaymentsAPI.status(sessionId);
        setData(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          // refresh user + usage so dashboard shows upgraded plan
          try {
            const me = await AuthAPI.me();
            setUser(me.data);
            await refreshUsage();
          } catch (refreshErr) {
            // Non-fatal: user is still paid, dashboard will fetch on next visit
            console.warn("Post-payment refresh failed:", refreshErr);
          }
          return;
        }
      } catch (pollErr) {
        // Transient network hiccup — will retry on next tick
        console.warn("Status poll attempt failed:", pollErr);
      }
      attemptsLocal += 1;
      setAttempts(attemptsLocal);
      if (attemptsLocal < 15) {
        setTimeout(poll, 2000);
      } else {
        setStatus("pending");
      }
    }
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const plan = data?.lookup_key ? PLAN_LABELS[data.lookup_key] : null;

  return (
    <div className="grain">
      <Navbar />
      <section className="relative min-h-[75vh] py-16">
        <div className="forge-glow absolute inset-0" />
        <div className="relative mx-auto max-w-2xl px-4 sm:px-6">
          <div className="rounded-3xl chrome-border p-10 text-center">
            {status === "loading" && (
              <>
                <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#4aa4ff]" />
                <h1 className="display-font mt-4 text-3xl font-bold">Confirming your payment…</h1>
                <p className="mt-2 text-sm text-muted-foreground">This usually takes a couple of seconds. Attempt {attempts}/15.</p>
              </>
            )}
            {status === "paid" && (
              <>
                <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#4aa4ff]/15 text-[#4aa4ff]">
                  <CheckCircle2 className="h-9 w-9" />
                </div>
                <h1 className="display-font mt-4 text-4xl font-bold">Payment complete.</h1>
                <p className="mt-2 text-muted-foreground">Your plan is active. Welcome to the club.</p>
                {plan && (
                  <div className="mx-auto mt-6 inline-flex items-center gap-2 rounded-full border border-[#4aa4ff]/40 bg-[#4aa4ff]/10 px-4 py-2 text-sm text-[#4aa4ff]">
                    <Crown className="h-4 w-4" /> {plan.name} · ${plan.price} / {plan.period}
                  </div>
                )}
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <Button className="btn-ember h-11 gap-2 px-5 font-semibold" onClick={() => navigate("/dashboard")}>
                    Go to dashboard <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" className="h-11" onClick={() => navigate("/studio/halftone")}><Sparkles className="mr-2 h-4 w-4" /> Start creating</Button>
                </div>
              </>
            )}
            {status === "pending" && (
              <>
                <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#f5c845]/15 text-[#f5c845]">
                  <KeyRound className="h-8 w-8" />
                </div>
                <h1 className="display-font mt-4 text-3xl font-bold">We’re still confirming with Stripe.</h1>
                <p className="mt-2 text-sm text-muted-foreground">Your payment looks in-flight. Your plan will unlock automatically once Stripe confirms — usually within a minute. You can safely close this page.</p>
                <Button className="btn-ember mt-6 h-11" onClick={() => window.location.reload()}>Check again</Button>
              </>
            )}
            {status === "error" && (
              <>
                <h1 className="display-font mt-4 text-3xl font-bold">Missing session</h1>
                <p className="mt-2 text-sm text-muted-foreground">We couldn’t find a checkout session in the URL. If you just paid, please email us and we’ll fix it right away.</p>
                <Button className="btn-ember mt-6 h-11" onClick={() => navigate("/pricing")}>Back to pricing</Button>
              </>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
